import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import type { DailyBreakdown } from "../types";
import { useLocale } from "../../../context/LanguageContext";

// ---------------------------------------------------------------------------
// Engagement Rate Chart (replaces Active Members count chart)
// ---------------------------------------------------------------------------

interface EngagementRateChartProps {
  data: DailyBreakdown[];
  loading?: boolean;
}

export function EngagementRateChart({
  data,
  loading = false,
}: EngagementRateChartProps) {
  const { t } = useLocale();

  if (loading) {
    return <ChartSkeleton title={t("dashboard.engagementRate")} />;
  }

  if (data.length === 0) {
    return <ChartEmpty title={t("dashboard.engagementRate")} />;
  }

  const chartData = data.map((d) => ({
    date: formatShortDate(d.date),
    rate:
      d.totalMembers > 0
        ? Math.round((d.activeMembers / d.totalMembers) * 100 * 10) / 10
        : 0,
  }));

  return (
    <div className="rounded-lg border border-border bg-card p-6">
      <h3 className="mb-4 text-sm font-semibold text-foreground">
        {t("dashboard.engagementRate")}
      </h3>
      <ResponsiveContainer width="100%" height={260}>
        <LineChart data={chartData}>
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="var(--color-border)"
          />
          <XAxis
            dataKey="date"
            fontSize={11}
            tickLine={false}
            axisLine={false}
            tick={{ fill: "var(--color-muted-foreground)" }}
          />
          <YAxis
            fontSize={11}
            tickLine={false}
            axisLine={false}
            tick={{ fill: "var(--color-muted-foreground)" }}
            domain={[0, 100]}
            tickFormatter={(v) => `${v}%`}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: "var(--color-card)",
              border: "1px solid var(--color-border)",
              borderRadius: "8px",
              color: "var(--color-foreground)",
              fontSize: 12,
            }}
            formatter={(value) => [`${Number(value)}%`, t("dashboard.engagementTooltip")]}
          />
          <Line
            type="monotone"
            dataKey="rate"
            name={t("dashboard.engagementRate")}
            stroke="var(--color-primary)"
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4, strokeWidth: 0, fill: "var(--color-primary)" }}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Steps Chart
// ---------------------------------------------------------------------------

interface StepsChartProps {
  data: DailyBreakdown[];
  loading?: boolean;
}

export function StepsChart({ data, loading = false }: StepsChartProps) {
  const { t } = useLocale();

  if (loading) {
    return <ChartSkeleton title={t("dashboard.totalSteps")} />;
  }

  if (data.length === 0) {
    return <ChartEmpty title={t("dashboard.totalSteps")} />;
  }

  const chartData = data.map((d) => ({
    date: formatShortDate(d.date),
    totalSteps: d.totalSteps,
  }));

  return (
    <div className="rounded-lg border border-border bg-card p-6">
      <h3 className="mb-4 text-sm font-semibold text-foreground">
        {t("dashboard.totalSteps")}
      </h3>
      <ResponsiveContainer width="100%" height={260}>
        <LineChart data={chartData}>
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="var(--color-border)"
          />
          <XAxis
            dataKey="date"
            fontSize={11}
            tickLine={false}
            axisLine={false}
            tick={{ fill: "var(--color-muted-foreground)" }}
          />
          <YAxis
            fontSize={11}
            tickLine={false}
            axisLine={false}
            tick={{ fill: "var(--color-muted-foreground)" }}
            tickFormatter={formatCompactNumber}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: "var(--color-card)",
              border: "1px solid var(--color-border)",
              borderRadius: "8px",
              color: "var(--color-foreground)",
              fontSize: 12,
            }}
            formatter={(value) => [
              Number(value).toLocaleString(),
              t("dashboard.stepsTooltip"),
            ]}
          />
          <Line
            type="monotone"
            dataKey="totalSteps"
            name={t("dashboard.stepsTooltip")}
            stroke="var(--color-secondary)"
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4, strokeWidth: 0, fill: "var(--color-secondary)" }}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

function formatShortDate(dateStr: string): string {
  const [, m, d] = dateStr.split("-");
  return `${d}/${m}`;
}

function formatCompactNumber(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(0)}K`;
  return String(value);
}

export function ChartSkeleton({ title }: { title: string }) {
  return (
    <div className="rounded-lg border border-border bg-card p-6">
      <h3 className="mb-4 text-sm font-semibold text-foreground">{title}</h3>
      <div className="flex h-[260px] items-center justify-center">
        <div className="h-full w-full animate-pulse rounded bg-muted" />
      </div>
    </div>
  );
}

export function ChartEmpty({ title }: { title: string }) {
  const { t } = useLocale();
  return (
    <div className="rounded-lg border border-border bg-card p-6">
      <h3 className="mb-4 text-sm font-semibold text-foreground">{title}</h3>
      <div className="flex h-[260px] items-center justify-center text-sm text-muted-foreground">
        {t("dashboard.noData")}
      </div>
    </div>
  );
}
